import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguageContext } from '@/i18n/LanguageContext';
import { courseDetails } from '@/mocks/pricingDetailsData';

const images = [
  '/images/generated/pricing-detail-essential-2026.jpg',
  '/images/generated/pricing-detail-advanced-2026.jpg',
  '/images/generated/pricing-detail-master-2026.jpg',
];

const catIcons = [
  'ri-presentation-line',
  'ri-book-2-line',
  'ri-tools-line',
  'ri-gift-line',
  'ri-award-line',
];

export default function CourseDetailCards() {
  const { t } = useTranslation();
  const { lang } = useLanguageContext();
  return (
    <section className="relative py-20 md:py-28 bg-background-50">
      <div className="max-w-[1600px] mx-auto px-8 md:px-12 lg:px-16">
        <div className="text-center mb-14">
          {String(t('pricingDetails.cards.label')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-accent-600 text-[13px] tracking-[0.2em] mb-5 font-label">{__s}</p>))}
          <h2 className="font-heading text-xl md:text-2xl text-foreground-900 tracking-[0.06em] mb-2">
            {t('pricingDetails.cards.title')}
          </h2>
          {String(t('pricingDetails.cards.titleEn')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="section-title-en text-accent-500 text-[15px] tracking-[0.12em] mb-4">{__s}</p>))}
          <div className="gold-line mx-auto"></div>
          {String(t('pricingDetails.cards.lead')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[15px] md:text-[16px] text-foreground-500 tracking-[0.04em] leading-relaxed mt-6 max-w-[700px] mx-auto">{__s}</p>))}
        </div>

        <div className="space-y-10 md:space-y-14">
          {courseDetails.map((course, idx) => (
            <div key={course.id} className={`${idx === 1 ? 'bg-primary-900 py-10 md:py-14 -mx-8 px-8' : ''}`}>
              <div className="max-w-[1000px] mx-auto">
                <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
                  <div className="lg:w-[40%] w-full">
                    <div className="w-full h-[220px] md:h-[280px] overflow-hidden mb-4">
                      <img
                        src={images[idx]}
                        alt={t(`pricingDetails.cards.img${idx}Alt`)}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <div className={`text-center ${idx === 1 ? 'text-primary-200' : 'text-foreground-500'}`}>
                      <p className="text-[13px] tracking-[0.04em]">
                        {t(`pricingDetails.cards.img${idx}Caption`)}
                      </p>
                    </div>
                  </div>

                  <div className="lg:w-[60%]">
                    <div className="flex items-center gap-4 mb-3 flex-wrap">
                      <span className={`text-[13px] tracking-[0.1em] px-3 py-1 border font-label ${
                        idx === 1
                          ? 'text-accent-400 border-accent-400/30 bg-accent-400/5'
                          : 'text-accent-600 border-accent-500/20 bg-accent-500/5'
                      }`}>
                        0{idx + 1}
                      </span>
                      <h3 className={`font-heading text-[18px] md:text-[20px] tracking-[0.06em] ${
                        idx === 1 ? 'text-foreground-50' : 'text-foreground-900'
                      }`}>
                        {lang === 'en' ? t(`pricingDetails.course.${idx}.name`) : course.name}
                      </h3>
                      <p className={`text-[13px] tracking-[0.08em] ${
                        idx === 1 ? 'text-primary-300' : 'text-accent-500'
                      }`}>
                        {course.nameEn}
                      </p>
                    </div>

                    <p className={`text-[15px] tracking-[0.04em] leading-relaxed mb-5 ${
                      idx === 1 ? 'text-foreground-300' : 'text-foreground-600'
                    }`}>
                      {lang === 'en' ? t(`pricingDetails.course.${idx}.lead`) : course.lead}
                    </p>

                    <div className="flex flex-wrap items-center gap-3 mb-5 text-[14px]">
                      <span className={`flex items-center gap-1 ${idx === 1 ? 'text-foreground-400' : 'text-foreground-500'}`}>
                        <i className="ri-calendar-line text-[13px]"></i>
                        {lang === 'en' ? t(`pricingDetails.course.${idx}.duration`) : course.duration}
                      </span>
                      <span className={`${idx === 1 ? 'text-foreground-600' : 'text-foreground-400'}`}>|</span>
                      <span className={`flex items-center gap-1 ${idx === 1 ? 'text-foreground-400' : 'text-foreground-500'}`}>
                        <i className="ri-time-line text-[13px]"></i>
                        {lang === 'en' ? t(`pricingDetails.course.${idx}.days`) : course.days}
                      </span>
                      <span className={`${idx === 1 ? 'text-foreground-600' : 'text-foreground-400'}`}>|</span>
                      <span className={`flex items-center gap-1 ${idx === 1 ? 'text-foreground-400' : 'text-foreground-500'}`}>
                        <i className="ri-book-open-line text-[13px]"></i>
                        {lang === 'en' ? t(`pricingDetails.course.${idx}.hours`) : course.hours}
                      </span>
                    </div>

                    <div className="mb-5">
                      <div className="flex items-baseline gap-2 mb-4">
                        <span className={`text-[24px] font-medium tracking-[0.06em] ${idx === 1 ? 'text-accent-400' : 'text-accent-600'}`}>
                          {course.price.toLocaleString()}{lang === 'en' ? ' JPY' : '円'}
                        </span>
                        <span className={`text-[13px] ${idx === 1 ? 'text-primary-300' : 'text-foreground-400'}`}>
                          {t('pricingDetails.cards.taxIncluded')}
                        </span>
                      </div>

                      <div className="space-y-3">
                        {course.categories.map((cat, catIdx) => (
                          <div key={catIdx} className={`p-3 ${
                            idx === 1
                              ? 'bg-primary-800/30 border border-primary-700/30'
                              : 'bg-background-100/50 border border-background-200/60'
                          }`}>
                            <div className="flex items-center gap-2 mb-2">
                              <div className={`w-5 h-5 flex items-center justify-center flex-shrink-0 ${
                                idx === 1 ? 'text-accent-400' : 'text-accent-500'
                              }`}>
                                <i className={`text-xs ${catIcons[catIdx] || 'ri-list-check'}`}></i>
                              </div>
                              <span className={`text-[15px] tracking-[0.05em] font-medium ${
                                idx === 1 ? 'text-foreground-100' : 'text-foreground-800'
                              }`}>
                                {lang === 'en' ? t(`pricingDetails.course.${idx}.cat.${catIdx}.label`) : cat.label}
                              </span>
                            </div>
                            <ul className={`space-y-1 pl-7 text-[14px] tracking-[0.03em] leading-relaxed ${
                              idx === 1 ? 'text-foreground-400' : 'text-foreground-500'
                            }`}>
                              {cat.items.map((item, itemIdx) => (
                                <li key={itemIdx} className="flex items-start gap-1.5">
                                  <span className={`flex-shrink-0 mt-1 w-1 h-1 rounded-full ${
                                    idx === 1 ? 'bg-primary-400' : 'bg-accent-500/40'
                                  }`}></span>
                                  {lang === 'en' ? t(`pricingDetails.course.${idx}.cat.${catIdx}.items.${itemIdx}`) : item}
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className={`flex items-center gap-2 pt-3 border-t ${
                      idx === 1 ? 'border-primary-700/30' : 'border-background-200/60'
                    }`}>
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                        <i className={`ri-verified-badge-line text-sm ${idx === 1 ? 'text-accent-400' : 'text-accent-500'}`}></i>
                      </div>
                      <div>
                        {String(t('pricingDetails.cards.afterExam')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className={`text-[14px] tracking-[0.03em] ${idx === 1 ? 'text-foreground-300' : 'text-foreground-500'}`}>{__s}</p>))}
                        <p className={`text-[15px] tracking-[0.05em] font-medium ${idx === 1 ? 'text-foreground-100' : 'text-foreground-800'}`}>
                          {lang === 'en' ? t(`pricingDetails.course.${idx}.qualification`) : course.qualification}
                        </p>
                        <p className={`text-[12px] tracking-[0.04em] ${idx === 1 ? 'text-primary-400' : 'text-accent-500'}`}>
                          {course.qualificationEn}
                        </p>
                      </div>
                    </div>

                    <Link
                      to={`/courses/${course.id}`}
                      className={`inline-flex items-center gap-2 mt-4 text-[14px] tracking-[0.05em] ${
                        idx === 1 ? 'text-accent-400 hover:text-accent-300' : 'text-accent-600 hover:text-accent-500'
                      } transition-colors`}
                    >
                      <span>{t('pricingDetails.cards.courseLink')}</span>
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-arrow-right-line text-xs"></i>
                      </div>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
